import { selectProfile } from '~/platform/user/selectors';
import { selectEhrDataByVhaId } from '~/platform/site-wide/drupal-static-data/source-files/vamc-ehr/selectors';

/**
 * Computes the signals for the single OH access rule (#150770).
 *
 * A user is blocked when they are an "Oracle Health only, no correlation" user:
 *   1. exactly one treatment facility (isSingle), AND
 *   2. that facility is a live Oracle Health facility per Drupal vamc-ehr
 *      (isOh — `ehr === 'cerner'`, excludes `cerner_staged`/pretransitioned), AND
 *   3. no 200CRNR patient correlation (noCorrelation — `isCernerPatient === false`).
 *
 * Fails open: while profile facilities or Drupal EHR data are not yet loaded,
 * `blocked` is false.
 *
 * @param {Object} state Redux state
 * @returns {{blocked: boolean, isSingle: boolean, isOh: boolean,
 *   noCorrelation: boolean, facilityId: (string|undefined), facilityCount: number}}
 */
export const selectOhOnlyAccessSignals = state => {
  const profile = selectProfile(state);
  const facilities = profile?.facilities;

  const facilityCount = facilities?.length ?? 0;
  const isSingle = facilityCount === 1;
  const only = facilities?.[0];

  const ehrDataByVhaId = selectEhrDataByVhaId(state);
  const isOh = ehrDataByVhaId[only?.facilityId]?.ehr === 'cerner';

  const noCorrelation = profile?.isCernerPatient === false;

  return {
    blocked: Boolean(isSingle && isOh && noCorrelation),
    isSingle,
    isOh,
    noCorrelation,
    facilityId: only?.facilityId,
    facilityCount,
  };
};

/**
 * Returns true when the user should be blocked by the single OH access rule.
 *
 * @param {Object} state Redux state
 * @returns {boolean} true when the user is blocked
 */
export const selectBlockedFromOhOnly = state =>
  selectOhOnlyAccessSignals(state).blocked;
